import './header.scss'
import { CmsLinkComponent } from '@cms/components/cmsLink/CmsLink'
import { Logo } from '@common/components/logo'
import { observe } from 'mobx'
import { observer } from 'mobx-react-lite'
import type { FC, RefObject } from 'react'
import { useEffect, useRef, useState } from 'react'
import { useLocation } from 'react-router-dom-v7'
import { breakpoints } from '@/common/consts/breakpoints'
import { useB2BHeaderAndFooterData } from '@/common/hooks/useB2BHeaderAndFooterData'
import { useDisableScrollForModal } from '@/common/hooks/useDisableScrollForModal'
import { useInitialData } from '@/common/hooks/useInitialData'
import { usePreventScroll } from '@/common/hooks/usePreventScroll'
import { useScreenWidth } from '@/common/hooks/useScreenWidth'
import { GlobalAnnouncementComponent } from '@/content/components/globalAnnouncement/GlobalAnnouncementComponent'
import { cmsDataService } from '@/content/services/cms.data.service'
import { navigationService } from '@/content/services/navigation.service'
import type { B2BHeaderAndFooterPropsApi } from '@/features/b2b/b2bHeaderAndFooter.models'
import { HelloBar } from '../../components/helloBar/HelloBar'
import { LanguagePickerOptions } from './languagePickerOptions/LanguagePickerOptions'
import { Burger, MemberStatus, Navigation, Search } from './subcomponents'
import type { HeaderLinksPropsApi } from './subcomponents/headerLinks/models'
import { MegaMenu } from './subcomponents/headerLinks/subcomponents/MegaMenu'

const pagesWithHero = [
    'AllCinemas',
    'BookingConfirmation',
    'OffersAndCompetitionsLanding',
    'LimitlessNewSubscriptionSignUp',
    'GiftStore',
]

const pagesWithoutNav = [
    'Ticketing',
    'BookingConfirmation',
    'Wifi',
    'Connected',
    'B2BDelegateWifi',
    'B2BDelegateWifiConnected',
    'FoodAndBeverage',
    'GiftStore',
]

const b2bPages = ['B2B', 'B2BEvents', 'B2BEvent', 'B2BContent', 'B2BVenue', 'B2BClosedVenue', '2BVenueFinder']

const getScrollThreshold = () => {
    const currentPage = navigationService.getCurrentPage()
    let isHomepage = false
    let isMobile = false
    if (window.innerWidth < 768) {
        isMobile = true
    }

    if (currentPage === 'Home') {
        isHomepage = true
    }

    if (isHomepage) {
        return isMobile ? 145 : 165
    }
    return 85
}

// eslint-disable-next-line complexity -- complex
export const Header: FC = () => {
    const [isOpen, setIsOpen] = useState<boolean>(false)
    const [hasHero, setHasHero] = useState<boolean>(false)
    const [hasNav, setHasNav] = useState<boolean>(false)
    const [isB2B, setIsB2B] = useState<boolean>(false)
    const [isResolved, setIsResolved] = useState<boolean>(false)
    const [selectedLink, setSelectedLink] = useState<HeaderLinksPropsApi | null>(null)
    const [page, setPage] = useState<string>()
    const [showSearch, setShowSearch] = useState<boolean>(false)
    const [lastScrollY, setLastScrollY] = useState<number>(0)
    const [show, setShow] = useState<boolean>(true)
    const [hasScrolled, setHasScrolled] = useState<boolean>(false)
    const [isAlertsOnScreen, setIsAlertsOnScreen] = useState<boolean>(true)
    const [focusNextKey, setFocusNextKey] = useState<string | null>(null)
    const screenWidth = useScreenWidth()
    const isDesktopUp = screenWidth >= breakpoints.desktop
    const navItemRefs = useRef<Map<string, HTMLButtonElement | HTMLAnchorElement>>(new Map())

    const closeModal = () => {
        setIsOpen(false)
        setSelectedLink(null)
    }
    const location = useLocation()
    const { displaySignInIcon } = useInitialData()

    const b2bHeaderAndFooterData = useB2BHeaderAndFooterData()

    const controlNavbar = () => {
        const alertsHeight: number = document.getElementById('priority-alerts')?.scrollHeight ?? 0
        const currentScrollY: number = window.scrollY
        const scrollThreshold: number = getScrollThreshold()

        if (currentScrollY > alertsHeight) {
            setIsAlertsOnScreen(false)

            if (currentScrollY > lastScrollY && currentScrollY > scrollThreshold) {
                setShow(false)
                setSelectedLink(null)
            } else {
                setShow(true)
            }
            setLastScrollY(currentScrollY)
            setHasScrolled(true)
        } else {
            setIsAlertsOnScreen(true)
            setHasScrolled(false)
        }
    }

    useEffect(() => {
        if (!focusNextKey) return

        requestAnimationFrame(() => {
            const el = navItemRefs.current.get(focusNextKey)
            el?.focus()
            setFocusNextKey(null)
        })
    }, [focusNextKey])

    useEffect(() => {
        window.addEventListener('scroll', controlNavbar)
        return () => {
            window.removeEventListener('scroll', controlNavbar)
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps -- controlNavbar
    }, [lastScrollY])

    useEffect(() => {
        closeModal()
    }, [location.key])

    useEffect(() => {
        setPage(navigationService.pageObs.get() as string)
        const unobserve = observe(navigationService.pageObs, ({ newValue }) => {
            setTimeout(() => {
                setIsOpen(false)
                setPage(newValue as string)
            }, 1)
        })

        return () => {
            unobserve()
        }
    }, [])

    useEffect(() => {
        if (page) {
            setHasHero(pagesWithHero.includes(page))
            setHasNav(!pagesWithoutNav.includes(page))
            setIsB2B(b2bPages.includes(page))

            setIsResolved(true)

            if (page === 'Home') {
                setShow(true)
            }
        }
    }, [page])
    usePreventScroll(isOpen, {
        maxVisibleBreakpoint: breakpoints.desktop,
        onClose: closeModal,
    })
    useDisableScrollForModal(isOpen, [], '.header-container')

    const isOpenClass = isOpen ? ' open' : ''
    const hasHeroClass = hasHero ? ' hero-header' : ''
    const hasNavClass = hasNav ? '' : ' header-without-nav'
    const isIOS15 = navigator.userAgent.includes('Version/15') ? ' height-auto' : ''
    const isB2bClass = isB2B ? ' b2b-header' : ''
    const isSearching = showSearch ? ' search-open' : ''
    const isStickyClass = !isAlertsOnScreen ? ' header--sticky' : ''
    const isVisibleClass = show ? ' header--sticky--visible' : ' header--sticky--hidden'
    const ignoreGradient = hasScrolled || selectedLink ? ' ignore-gradient' : ''

    const pageHeaderClass = (() => {
        switch (page) {
            case 'Home':
                return ' home-page'
            case 'ReviewTicket':
                return ' review-ticket'
            case 'Wifi':
                return ' wifi'
            case 'Connected':
                return ' connected'
            case 'B2BDelegateWifi':
                return ' b2b-wifi'
            case 'B2BDelegateWifiConnected':
                return ' b2b-wifi-connected'
            default:
                return ''
        }
    })()

    const burgerClickHandler = () => {
        if (isOpen) setSelectedLink(null)
        setIsOpen(!isOpen)
    }

    if (!isResolved) return null // Don't load before isB2B is properly resolved

    return (
        <>
            <div id="priority-alerts" className={isSearching}>
                <HelloBar />
                {!isSearching && <GlobalAnnouncementComponent />}
            </div>

            <header className={`header-wrapper${hasHeroClass}${isIOS15}${isB2bClass}`}>
                <div
                    className={`header-container${isOpenClass}${hasNavClass}${pageHeaderClass}${isVisibleClass}${ignoreGradient}${isStickyClass}${isSearching}`}
                >
                    <div className="header container-lg-padding">
                        {isB2B ? (
                            <B2BCta
                                isB2B={isB2B}
                                b2bHeaderAndFooterData={b2bHeaderAndFooterData}
                                selectedLink={selectedLink}
                                setSelectedLink={setSelectedLink}
                                navItemRefs={navItemRefs}
                            />
                        ) : (
                            <GeneralNavigation
                                hasNav={hasNav}
                                toggleSearch={setShowSearch}
                                isB2B={isB2B}
                                selectedLink={selectedLink}
                                setSelectedLink={setSelectedLink}
                                displaySignInIcon={displaySignInIcon}
                                navItemRefs={navItemRefs}
                            />
                        )}
                        <LanguagePickerOptions />
                        <Burger isOpen={isOpen} onClick={burgerClickHandler} />
                    </div>

                    {isDesktopUp && (
                        <MegaMenu
                            selectedLink={selectedLink}
                            isOpen={selectedLink !== null}
                            navItemRefs={navItemRefs}
                            onClose={() => setSelectedLink(null)}
                            setFocusNextKey={setFocusNextKey}
                        />
                    )}
                </div>
            </header>
        </>
    )
}

const GeneralNavigation = observer(
    ({
        hasNav = false,
        toggleSearch,
        isB2B = false,
        selectedLink = null,
        setSelectedLink,
        displaySignInIcon = true,
        navItemRefs,
    }: {
        hasNav: boolean
        toggleSearch: (boolean) => void
        isB2B?: boolean
        selectedLink?: HeaderLinksPropsApi | null
        setSelectedLink: (link: HeaderLinksPropsApi | null) => void
        displaySignInIcon?: boolean
        navItemRefs: RefObject<Map<string, HTMLButtonElement | HTMLAnchorElement>>
    }) => {
        return (
            <>
                <Logo />
                <Navigation
                    isB2B={isB2B}
                    selectedLink={selectedLink}
                    setSelectedLink={setSelectedLink}
                    navItemRefs={navItemRefs}
                />
                {displaySignInIcon && <MemberStatus />}
                {hasNav && <Search toggleSearchOpenCallback={toggleSearch} />}
            </>
        )
    }
)

const B2BCta: FC<{
    isB2B?: boolean
    b2bHeaderAndFooterData?: B2BHeaderAndFooterPropsApi
    selectedLink?: HeaderLinksPropsApi | null
    setSelectedLink: (link: HeaderLinksPropsApi | null) => void
    navItemRefs: RefObject<Map<string, HTMLButtonElement | HTMLAnchorElement>>
}> = ({ isB2B = false, selectedLink = null, setSelectedLink, b2bHeaderAndFooterData, navItemRefs }) => {
    const b2bCtaLink = cmsDataService.get()?.header?.b2BCTAButton
    return (
        <>
            <Logo isB2B={true} b2bHeaderAndFooterData={b2bHeaderAndFooterData} />
            <Navigation
                isB2B={isB2B}
                b2bHeaderAndFooterData={b2bHeaderAndFooterData}
                selectedLink={selectedLink}
                setSelectedLink={setSelectedLink}
                navItemRefs={navItemRefs}
            />
            {b2bCtaLink && (
                <div className="b2b-cta-link-container">
                    <CmsLinkComponent className="btn btn--primary" link={b2bCtaLink} />
                </div>
            )}
        </>
    )
}
